import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Facet, Sorting, Value } from '../../search-spring/types/types'
import Filter from '../../search-spring-page/components/Filter'
import ReadyToShipFilter from '../../search-spring-page/components/ReadyToShipFilter'
import SubCollectionFilter from '../../search-spring-page/components/SubCollectionFilter'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'

type Props = {
  filters: Facet[]
  sorting: Sorting
  triggerSearch: () => void
  activeFilters: number
}

type SubCollectionLinks = {
  label: string
  url: string
}

declare global {
  interface Window {
    collectionDescription: string;
    collectionDescriptionMobile: string;
    subCollectionItems: SubCollectionLinks[]
  }
}


const CollectionPageHeader = ({ filters, sorting, triggerSearch, activeFilters}: Props) => {
  const hasReadyToShipFilter = filters.some(filter => filter.values && filter.values.find(label => label.value == "Ready to Ship"))
  const hasGlobalCollectionSubfilters = CD_STATE?.collectionDisrupter.subCollectionItems
  const hasLocalCollectionSubFilters = CD_STATE?.collectionDisrupter.localSubCollectionItems
  const bannerDesktop = CD_STATE?.collectionDisrupter.banner_desktop
  const bannerMobile = CD_STATE?.collectionDisrupter.banner_mobile ?? bannerDesktop
  const bannerTextColor = CD_STATE?.collectionDisrupter.banner_text_color ?? '#000000'

  const windowSize = useWindowSize()
  const isTablet = windowSize.width <= 1024
  const scrollableDivRef = useRef<HTMLDivElement>(null)
  const [displayMore, setDisplayMore] = useState(false)
  const [showEllipsis, setShowEllipsis] = useState(false)
  let textDescription

  if (windowSize.width < 768 && CD_STATE.collectionDisrupter?.collectionDescriptionMobile !== '') {
    textDescription = CD_STATE.collectionDisrupter?.collectionDescriptionMobile || ''
  } else {
    textDescription = CD_STATE.collectionDisrupter?.collectionDescription || ''
  }

  const moreIndex = textDescription.indexOf('[more]')
  const hasMore = moreIndex !== -1
  const textBeforeMore = hasMore ? (textDescription.slice(0, moreIndex) + (showEllipsis && !displayMore ? '...' : '')) : textDescription
  const textAfterMore = hasMore ? textDescription.slice(moreIndex + 6) : ""

  const memoizedTriggerSearch = useCallback(() => {
    triggerSearch()
  }, [triggerSearch])

    // Draggable functionality
    useEffect(() => {
      const scrollableDiv = scrollableDivRef.current
      if (!scrollableDiv) return
  
      let isDragging = false
      let startX = 0
      let scrollLeft = 0
  
      const handleMouseDown = (e: MouseEvent) => {
        isDragging = true
        startX = e.pageX - scrollableDiv.offsetLeft
        scrollLeft = scrollableDiv.scrollLeft
        scrollableDiv.style.cursor = 'grabbing'
      };
  
      const handleMouseLeave = () => {
        isDragging = false
        scrollableDiv.style.cursor = 'grab'
      };
  
      const handleMouseUp = () => {
        isDragging = false
        scrollableDiv.style.cursor = 'grab'
      };
  
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        const x = e.pageX - scrollableDiv.offsetLeft
        const walk = (x - startX) * 2
        scrollableDiv.scrollLeft = scrollLeft - walk
      }
  
      scrollableDiv.addEventListener('mousedown', handleMouseDown)
      scrollableDiv.addEventListener('mouseleave', handleMouseLeave)
      scrollableDiv.addEventListener('mouseup', handleMouseUp)
      scrollableDiv.addEventListener('mousemove', handleMouseMove)
  
      return () => {
        scrollableDiv.removeEventListener('mousedown', handleMouseDown)
        scrollableDiv.removeEventListener('mouseleave', handleMouseLeave)
        scrollableDiv.removeEventListener('mouseup', handleMouseUp)
        scrollableDiv.removeEventListener('mousemove', handleMouseMove)
      }
    }, [])

  // The collections have pre populated quick link call outs from metafields
  const subCollectionHandler = (): JSX.Element => {
    const quickLinksToRender: JSX.Element[] = []


    if (hasLocalCollectionSubFilters.length) {
      hasLocalCollectionSubFilters?.map((collection: SubCollectionLinks) => {
        quickLinksToRender.push(<SubCollectionFilter collectionName={collection.label} collectionUrl={collection.url} fromMetaObject={true} />)
      })
    } else {
      hasGlobalCollectionSubfilters?.map((collection: SubCollectionLinks) => {
        quickLinksToRender.push(<SubCollectionFilter collectionName={collection.label} collectionUrl={collection.url} />)
      })
    }

    return (
      <>
      <div ref={scrollableDivRef} className={`flex flex-grow justify-between flex-shrink-0 scrollable ${isTablet ? '' : 'overflow-x-auto relative max-w-[98%]'}`}>
        <div className="flex items-center font-light" style={{ color: CD_STATE?.collectionDisrupter.banner_text_color ?? '#949494' }}>
          {quickLinksToRender.map((component, index) => (
            <div key={index}>{component}</div>
          ))}
        </div>
      </div>
      </>
    )
  }

  useEffect(() => {
    if (windowSize.width < 768) {
      setShowEllipsis(true)
    } else setShowEllipsis(false)
  }, [])

  const minHeight = bannerDesktop || bannerMobile ? 'min-h-[380px] lg:min-h-[500px]' : ''

  return (
    <>
      <div className={`relative mb-[10px] pb-[10px] pt-[15px] lg:pt-[20px] px-[10px] ${minHeight} flex flex-col justify-between gap-[40px]`} style={{ color: bannerTextColor }}>
        {bannerDesktop && <img
          src={bannerDesktop}
          alt=""
          aria-hidden="true"
          className="absolute lg:block hidden inset-0 w-full h-full object-cover z-[-1] pointer-events-none select-none"
        />}
        {bannerMobile && <img
          src={bannerMobile}
          alt=""
          aria-hidden="true"
          className="absolute block lg:hidden inset-0 w-full h-full object-cover z-[-1] pointer-events-none select-none"
        />}
 
        <div className="flex flex-col gap-[10px]">
          <h1 data-nosnippet className="uppercase h2">{`${CD_STATE.searchspring.collectionName ? CD_STATE.searchspring.collectionName : ''}`}</h1>
          <div className='lg:w-1/2'>
            {textBeforeMore && <div className='utility-2 font-bstSpyre [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0' dangerouslySetInnerHTML={{ __html: displayMore ? textBeforeMore + textAfterMore : textBeforeMore }}></div>}
            {hasMore && (
              <button className='mt-2.5 underline utility-2 font-bstSpyre' onClick={() => setDisplayMore(!displayMore)}>
                {displayMore ? CD_STATE.collectionDisrupter?.readLess : CD_STATE.collectionDisrupter?.readMore}
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-[20px] flex-wrap justify-between utility-2">
          <div className="flex flex-grow overflow-x-auto custom-scrollbar">
            <Filter filters={filters} sorting={sorting} triggerSearch={memoizedTriggerSearch} activeFilters={activeFilters} />
            {hasGlobalCollectionSubfilters || hasLocalCollectionSubFilters ? subCollectionHandler() : null}
          </div>
          {hasReadyToShipFilter && <ReadyToShipFilter />}
        </div>
      </div>
    </>
  )
}

export default CollectionPageHeader